import { AsyncPipe, CommonModule, CurrencyPipe, DatePipe, JsonPipe } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule, MatDateRangeInput } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  IGX_GRID_DIRECTIVES,
  IgxActionStripModule,
  IgxExcelExporterOptions,
  IgxExcelExporterService,
  IgxGridComponent,
  IgxGridModule,
  IgxSwitchModule,
  IgxTabsModule,
  IPinningConfig,
  RowPinningPosition,
} from '@infragistics/igniteui-angular';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { IAgingCommentsDialogUpdate, IInvoiceAsOfAgingResult } from 'src/app/shared/interfaces';
import { HeaderTitleLotDataComponent } from 'src/app/shared/ui/header-title-lot-data/header-title-lot-data.component';
import { AgingCommentsUpdateComponent } from '../../dialog-report/aging-comments-update/aging-comments-update.component';
import { environment } from 'src/environments/environment';
import { IgxPreventDocumentScrollModule } from 'src/app/shared/directives/prevent-scroll.directive';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router } from '@angular/router';

@ViewChild('gridAgingReport', { static: true })
@Component({
  selector: 'app-report-aging',
  standalone: true,
  imports: [
    MatSlideToggleModule,
    MatExpansionModule,
    MatIconModule,
    CommonModule,
    MatTabsModule,
    MatSnackBarModule,
    MatTableModule,
    MatCardModule,
    MatOptionModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    IgxGridModule,
    IgxTabsModule,
    IgxGridComponent,
    HeaderTitleLotDataComponent,
    MatTooltipModule,
    MatButtonModule,
    MatDialogModule,
    IgxActionStripModule,
    IgxSwitchModule,
    IgxPreventDocumentScrollModule,
    CurrencyPipe,
    MatCheckboxModule,
    FormsModule,
  ],
  templateUrl: './report-aging.component.html',
  styleUrl: './report-aging.component.scss',
})
export class ReportAgingComponent implements OnInit, AfterViewInit {
  public gridAgingReport: IgxGridComponent;
  agingReportList: IInvoiceAsOfAgingResult[] = [];
  public pinningConfig: IPinningConfig = { rows: RowPinningPosition.Top };
  private readonly minimumSearchMessageMs: number = 4000;
  private searchMessageShownAt: number = 0;

  urlDomain: string = '';

  //Total Calc Vars
  totalCurrent: number = 0;
  totalZone1: number = 0;
  totalZone2: number = 0;
  totalZone3: number = 0;
  totalZone4: number = 0;
  totalZone5: number = 0;
  grandTotal: number = 0;

  //Services
  private dataService = inject(DataService);
  public _appGeneralState = inject(AppGeneralStateService);
  private _destroyRefService = inject(DestroyRef);
  _messageService = inject(MessageDialogService);
  _snackBar = inject(MatSnackBar);
  _dialog = inject(MatDialog);
  _datePipe = inject(DatePipe);
  private readonly router = inject(Router);
  showPinnedRow: boolean = false;

  asOfAgingForm = new FormGroup({
    asOfDate: new FormControl(new Date(), [Validators.required]),
  });
  datepipe: any;

  constructor() {
    this._appGeneralState.lotNumberSubject.subscribe({
      next: (v) => this.onLotNumberChange(v),
    });

    this.urlDomain = environment.UrlDomain;
  }

  togglePinnedRow(): void {
    this.showPinnedRow = !this.showPinnedRow;
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {}

  onLotNumberChange(lotNumber: any) {
    if (this.agingReportList.length > 0) {
      this._messageService.snackBarTopCenterTimed(
        'The lot number was changed and the data for the previous lot has been cleared.',
        3000,
      );
    }

    this.agingReportList = [];
  }

  onEditComments(invoiceNumber: string, comments: string): void {
    const enterAnimationDuration: string = '0ms';
    const exitAnimationDuration: string = '0ms';

    let currentCommentData: IAgingCommentsDialogUpdate = {} as IAgingCommentsDialogUpdate;
    currentCommentData.invoiceNumber = invoiceNumber;
    currentCommentData.comments = comments;

    const dialogRef = this._dialog.open(AgingCommentsUpdateComponent, {
      data: { invoiceNumber: invoiceNumber, comments: comments },
      minWidth: '900px',
      minHeight: '550px',
      enterAnimationDuration,
      exitAnimationDuration,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined) {
        this.onUpdateCommentsAfterChange(invoiceNumber, result);
      }
    });
  }

  onUpdateCommentsAfterChange(invoiceNumber: string, comments: string): void {
    let ii: number = 0;
    var datePipe = new DatePipe('en-US');
    const currentDate: any = datePipe.transform(new Date(), 'MM-dd-yyyy');
    let newCommentUpdate: string;

    if (comments != '' && comments != null) {
      newCommentUpdate = `${currentDate} - ${comments} - ${this._appGeneralState.userName}`;
    } else {
      newCommentUpdate = '';
    }
    for (ii = 0; ii < this.agingReportList.length; ii++) {
      if (this.agingReportList[ii].invoiceNumber == invoiceNumber) {
        this.agingReportList[ii].comments = newCommentUpdate;
        this.agingReportList[ii].commentsOnly = comments;
      }
    }
  }

  onSubmitAsOfAgingSentence(): void {
    const asOfDate: Date = new Date(this.asOfAgingForm.get('asOfDate').value);
    const lotNumber: string = this._appGeneralState.currentLotNumber;
    this.getAsOfAgingReportData(lotNumber, asOfDate);
  }

  getAsOfAgingReportData(lotNumber: string, asOfDate: Date) {
    this.agingReportList = [];
    this.displaySearchMessage();
    this.dataService
      .getAsOfAgingReport(lotNumber, asOfDate.toDateString())
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.agingReportList = res;
          console.log('Aging Report Data', this.agingReportList);
        },
        error: (err: any) => {
          this.dismissSearchMessageWithMinimumDuration(() => {
            this._messageService.snackBarTopCenterTimed(
              'An unexpected error occurred while loading the Aging report.  Please try again.',
              5000,
            );
          });
        },
        complete: () => {
          this.dismissSearchMessageWithMinimumDuration(() => {
            if (this.agingReportList.length == 0) {
              this._messageService.snackBarTopCenterTimed('No invoices were located for this lot.', 5000);
            } else {
              // this.isInvoiceNumberUnique();
              this.postFetchAgingReport();
              this.calcTotalsForHeader();
            }
          });
        },
      });
  }

  postFetchAgingReport(): void {
    let ii: number = 0;

    try {
      for (ii = 0; ii < this.agingReportList.length; ii++) {
        this.agingReportList[ii].theDueDate = this._datePipe.transform(this.agingReportList[ii].dueDate, 'yyyy-MM-dd');
      }
    } catch (err) {
      console.error('PostFetchAgingReport', err);
    }
  }

  calcTotalsForHeader(): void {
    let ii: number = 0;

    try {
      for (ii = 0; ii < this.agingReportList.length; ii++) {
        this.totalCurrent += this.agingReportList[ii].current;
        this.totalZone1 += this.agingReportList[ii].zone1;
        this.totalZone2 += this.agingReportList[ii].zone2;
        this.totalZone3 += this.agingReportList[ii].zone3;
        this.totalZone4 += this.agingReportList[ii].zone4;
        this.totalZone5 += this.agingReportList[ii].zone5;
      }

      this.grandTotal =
        this.totalCurrent + this.totalZone1 + this.totalZone2 + this.totalZone3 + this.totalZone4 + this.totalZone5;

      this.addTotalToAgingReport();
    } catch (err) {
      console.error('PostFetchAgingReport', err);
    }
  }

  addTotalToAgingReport(): void {
    let totalsRow: IInvoiceAsOfAgingResult = {} as IInvoiceAsOfAgingResult;

    try {
      totalsRow.parkerPayType = '';
      totalsRow.customerName = '';
      totalsRow.invoiceNumber = 'Totals';
      totalsRow.theDueDate = this.formatCurrency(this.grandTotal);
      totalsRow.current = this.totalCurrent;
      totalsRow.zone1 = this.totalZone1;
      totalsRow.zone2 = this.totalZone2;
      totalsRow.zone3 = this.totalZone3;
      totalsRow.zone4 = this.totalZone4;
      totalsRow.zone5 = this.totalZone5;

      this.agingReportList.push(totalsRow);

      //this.pinTotalsRow();
    } catch (err) {
      console.error('addTotalToAgingReport', err);
    }
  }

  pinTotalsRow(): void {
    if (this.agingReportList && this.agingReportList.length > 0) {
      this.gridAgingReport.pinRow(this.agingReportList[this.agingReportList.length - 1].invoiceNumber);
    }
  }

  formatCurrency(amount: number, currencyCode: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  }

  displaySearchMessage(): void {
    this.searchMessageShownAt = Date.now();
    const verticalPosition: MatSnackBarVerticalPosition = 'top';
    this._snackBar.open('Loading Aging Report...', '', { verticalPosition: verticalPosition });
  }

  private dismissSearchMessageWithMinimumDuration(afterDismiss?: () => void): void {
    const elapsedMs = this.searchMessageShownAt > 0 ? Date.now() - this.searchMessageShownAt : 0;
    const remainingMs = Math.max(0, this.minimumSearchMessageMs - elapsedMs);

    setTimeout(() => {
      this._snackBar.dismiss();
      this.searchMessageShownAt = 0;
      afterDismiss?.();
    }, remainingMs);
  }

  isInvoiceNumberUnique(): void {
    let ii: number;
    let previousInvoiceNumber: string = '';
    let isDuplicate: boolean = false;

    for (ii = 0; ii < this.agingReportList.length; ii++) {
      if (this.agingReportList[ii].invoiceNumber == previousInvoiceNumber) {
        isDuplicate = true;
      }
      previousInvoiceNumber = this.agingReportList[ii].invoiceNumber;
    }

    console.error('unique status', isDuplicate);
  }

  onDisplayInvoice(invoiceNumber: string, lotNumber: string, entityId: string): void {
    const safeEntityId = entityId && entityId.trim().length > 0 ? entityId : 'na';
    const invoiceViewUrl = this.router.serializeUrl(
      this.router.createUrlTree(['/viewinvoice', safeEntityId, lotNumber, invoiceNumber]),
    );
    window.open(invoiceViewUrl, '_blank');
  }
}
